import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILDING_TYPES = ["Residential Shop", "Commercial Shop", "Barndominium", "Ag / Barn", "Other"];
const OPTIONS = ["Insulation Package", "Overhead Doors", "Walk-In Doors", "Windows", "Lean-To / Awning", "Wainscoting", "Gutters & Downspouts"];
const ROOF_PITCHES = ["1:12 — Low Slope", "2:12", "3:12", "4:12 — Standard", "6:12 — Steep", "Not sure — recommend one for me"];

const inputCls =
  "h-12 rounded-none border-white/20 bg-transparent text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-turquoise focus-visible:border-turquoise";

const formatError = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", building_type: "",
    width: "", length: "", height: "", roof_pitch: "", options: [], notes: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const toggleOption = (opt) => {
    setForm((f) => ({
      ...f,
      options: f.options.some((o) => o.name === opt)
        ? f.options.filter((o) => o.name !== opt)
        : [...f.options, { name: opt, quantity: 1, size: "" }],
    }));
  };

  const updateOption = (opt, key, value) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o) => (o.name === opt ? { ...o, [key]: value } : o)),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.building_type) {
      toast.error("Please pick a building type.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        width: parseFloat(form.width),
        length: parseFloat(form.length),
        height: parseFloat(form.height),
      });
      toast.success("Quote request sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", building_type: "", width: "", length: "", height: "", roof_pitch: "", options: [], notes: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="border-t border-white/10 px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-turquoise">05 — Get a Quote</p>
          <h2 className="font-heading mt-4 text-3xl font-black uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Get a straight number <span className="text-volt">on your building</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
            Tell us the size, roof pitch, and the options you're after — with quantities and
            door or window sizes where you know them. We'll come back with a straight number
            for a clearly specced building — no fog, no fine print.
          </p>
          <ul className="mt-8 space-y-3 text-xs uppercase tracking-[0.2em] text-white/50">
            <li className="flex items-center gap-3"><span className="h-px w-6 bg-volt" /> Free, no-pressure estimates</li>
            <li className="flex items-center gap-3"><span className="h-px w-6 bg-volt" /> Engineered for your county's codes</li>
            <li className="flex items-center gap-3"><span className="h-px w-6 bg-volt" /> Supply-only or full erection</li>
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="border border-white/10 bg-surface/60 p-6 sm:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input data-testid="quote-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
              <Input data-testid="quote-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              <Input data-testid="quote-phone-input" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <Select value={form.building_type} onValueChange={(v) => setForm({ ...form, building_type: v })}>
                <SelectTrigger data-testid="quote-building-type-select" className={inputCls}>
                  <SelectValue placeholder="Building type" />
                </SelectTrigger>
                <SelectContent className="border-white/20 bg-surface text-white">
                  {BUILDING_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`quote-type-${t.toLowerCase().replace(/[\s/]+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">Building size (feet)</p>
            <div className="mt-3 grid grid-cols-3 gap-4">
              <Input data-testid="quote-width-input" required type="number" min="1" step="any" placeholder="Width" value={form.width} onChange={set("width")} className={inputCls} />
              <Input data-testid="quote-length-input" required type="number" min="1" step="any" placeholder="Length" value={form.length} onChange={set("length")} className={inputCls} />
              <Input data-testid="quote-height-input" required type="number" min="1" step="any" placeholder="Height" value={form.height} onChange={set("height")} className={inputCls} />
            </div>

            <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">Roof pitch</p>
            <Select value={form.roof_pitch} onValueChange={(v) => setForm({ ...form, roof_pitch: v })}>
              <SelectTrigger data-testid="quote-roof-pitch-select" className={`${inputCls} mt-3`}>
                <SelectValue placeholder="Choose a roof pitch (optional)" />
              </SelectTrigger>
              <SelectContent className="border-white/20 bg-surface text-white">
                {ROOF_PITCHES.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`quote-pitch-${p.split(":")[0]}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
              Options — check what you want, then add quantities &amp; sizes
            </p>
            <div className="mt-3 space-y-3">
              {OPTIONS.map((opt) => {
                const sel = form.options.find((o) => o.name === opt);
                const tid = opt.toLowerCase().replace(/[\s/&]+/g, "-");
                return (
                  <div key={opt} className="border border-white/10 p-3 transition-colors duration-300 hover:border-white/20">
                    <label className="flex cursor-pointer items-center gap-3 text-sm text-white/70">
                      <Checkbox
                        data-testid={`quote-option-${tid}`}
                        checked={!!sel}
                        onCheckedChange={() => toggleOption(opt)}
                        className="rounded-none border-white/30 data-[state=checked]:border-volt data-[state=checked]:bg-volt data-[state=checked]:text-black"
                      />
                      {opt}
                    </label>
                    {sel && (
                      <div className="mt-3 grid grid-cols-3 gap-3">
                        <Input
                          data-testid={`quote-option-qty-${tid}`}
                          type="number"
                          min="1"
                          placeholder="Qty"
                          value={sel.quantity}
                          onChange={(e) => updateOption(opt, "quantity", parseInt(e.target.value) || 1)}
                          className={`${inputCls} h-10`}
                        />
                        <Input
                          data-testid={`quote-option-size-${tid}`}
                          placeholder="Size (e.g. 12×14 ft)"
                          value={sel.size}
                          onChange={(e) => updateOption(opt, "size", e.target.value)}
                          className={`${inputCls} col-span-2 h-10`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Textarea
              data-testid="quote-notes-input"
              placeholder="Anything else we should know? (site location, timeline, door sizes...)"
              value={form.notes}
              onChange={set("notes")}
              className={`${inputCls} mt-8 h-28 resize-none py-3`}
            />

            <button
              data-testid="quote-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-8 flex w-full items-center justify-center gap-2 bg-volt px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-50"
            >
              {sending ? "Sending..." : "Request My Quote"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
